import { ISavV2, SavV2Status } from '../models/savV2';
import { ISavV2Intervention } from '../models/savV2Intervention';
import Contract from '../models/contract';
import { getVehicleInfo } from './contractHelpers';
import { getBeneficiaryInfo } from './beneficiaryHelpers';

/**
 * Récupère le label du statut SAV V2
 */
export function getSavStatusLabel(status: SavV2Status): string {
  const labels: Record<SavV2Status, string> = {
    [SavV2Status.DRAFT]: 'Brouillon',
    [SavV2Status.ACTIVE]: 'Actif',
    [SavV2Status.COMPLETED]: 'Terminé',
    [SavV2Status.CANCELLED]: 'Annulé',
  };

  return labels[status] || status;
}

/**
 * Récupère la couleur du badge pour un statut
 */
export function getSavStatusColor(status: SavV2Status): string {
  const colors: Record<SavV2Status, string> = {
    [SavV2Status.DRAFT]: 'tw-bg-yellow-100 tw-text-yellow-800',
    [SavV2Status.ACTIVE]: 'tw-bg-green-100 tw-text-green-800',
    [SavV2Status.COMPLETED]: 'tw-bg-blue-100 tw-text-blue-800',
    [SavV2Status.CANCELLED]: 'tw-bg-gray-100 tw-text-gray-800',
  };

  return colors[status] || 'tw-bg-gray-100 tw-text-gray-800';
}

/**
 * Récupère la localisation de la panne sous forme lisible
 */
export function getFaultLocationLabel(location: string | undefined): string {
  if (!location) return 'Non renseigné';

  const labels: Record<string, string> = {
    'parking': 'Parking',
    'street': 'Rue',
    'highway': 'Autoroute',
    'home': 'Domicile',
    'work': 'Travail',
    'garage': 'Garage',
    'other': 'Autre',
  };

  return labels[location] || location;
}

/**
 * Récupère le moment de la panne sous forme lisible
 */
export function getFaultOccurrenceLabel(occurrence: string | undefined): string {
  if (!occurrence) return 'Non renseigné';

  const labels: Record<string, string> = {
    'startup': 'Au démarrage',
    'driving': 'En roulant',
    'idle': 'À l\'arrêt',
    'parking': 'En stationnement',
    'other': 'Autre',
  };

  return labels[occurrence] || occurrence;
}

/**
 * Récupère la manifestation de la panne sous forme lisible
 */
export function getFaultManifestationLabel(manifestation: string | undefined): string {
  if (!manifestation) return 'Non renseigné';

  const labels: Record<string, string> = {
    'suddenly': 'Soudainement',
    'progressively': 'Progressivement',
    'intermittently': 'Par intermittence',
    'after_event': 'Après un événement',
    'other': 'Autre',
  };

  return labels[manifestation] || manifestation;
}

/**
 * Récupère les symptômes sous forme lisible
 */
export function getSymptomsLabels(symptoms: string[] | undefined): string[] {
  if (!symptoms || symptoms.length === 0) return [];

  const labels: Record<string, string> = {
    'engine_noise': 'Bruit moteur',
    'vibration': 'Vibrations',
    'smoke': 'Fumée',
    'warning_light': 'Voyant allumé',
    'loss_of_power': 'Perte de puissance',
    'starting_problem': 'Problème de démarrage',
    'overheating': 'Surchauffe',
    'leak': 'Fuite',
    'other': 'Autre',
  };

  return symptoms.map(s => labels[s] || s);
}

/**
 * Récupère l'état de réparation sous forme lisible
 */
export function getRepairStatusLabel(status: string | undefined): string {
  if (!status) return 'Non renseigné';

  const labels: Record<string, string> = {
    'not_started': 'Non démarré',
    'diagnosed': 'Diagnostiqué',
    'in_progress': 'En cours',
    'completed': 'Terminé',
    'waiting_parts': 'En attente de pièces',
  };

  return labels[status] || status;
}

/**
 * Récupère l'intervention courante d'un SAV
 */
export function getCurrentIntervention(interventions: ISavV2Intervention[]): ISavV2Intervention | null {
  if (!interventions || interventions.length === 0) return null;

  // Prendre la dernière intervention ou celle qui est en cours
  const inProgress = interventions.find(i => i.outcome === 'in_progress');
  return inProgress || interventions[interventions.length - 1];
}

/**
 * Récupère le label de l'outcome d'une intervention
 */
export function getInterventionOutcomeLabel(outcome: string): string {
  const labels: Record<string, string> = {
    'in_progress': 'En cours',
    'resolved': 'Résolu',
    'partial': 'Partiellement résolu',
    'failed': 'Échec',
  };

  return labels[outcome] || outcome;
}

/**
 * Récupère le label de la raison d'une intervention
 */
export function getInterventionReasonLabel(reason: string): string {
  const labels: Record<string, string> = {
    'initial': 'Initiale',
    'recurrence': 'Récurrence',
    'new_problem': 'Nouveau problème',
    'complementary': 'Complémentaire',
  };

  return labels[reason] || reason;
}

/**
 * Récupère toutes les informations utiles d'un SAV V2
 */
export function getSavV2FullInfo(sav: ISavV2 | null, contract?: Contract | null) {
  if (!sav) return null;

  const vehicleInfo = getVehicleInfo(contract || sav.contract);
  const beneficiaryInfo = getBeneficiaryInfo(contract?.beneficiary || sav.contract?.beneficiary);

  return {
    // Identifiants
    id: sav.id,
    uniqueId: sav.uniqueId,

    // Statut
    status: sav.status,
    statusLabel: getSavStatusLabel(sav.status),
    statusColor: getSavStatusColor(sav.status),

    // Véhicule
    vehicle: vehicleInfo,
    vehicleDescription: vehicleInfo.fullDescription,

    // Bénéficiaire
    beneficiary: beneficiaryInfo,

    // Détails de la panne
    fault: {
      description: sav.description,
      date: sav.incidentDate,
      mileage: sav.mileage,
      location: getFaultLocationLabel(sav.faultLocation),
      occurrence: getFaultOccurrenceLabel(sav.faultOccurrence),
      manifestation: getFaultManifestationLabel(sav.faultManifestation),
      symptoms: getSymptomsLabels(sav.symptoms),
    },

    // Localisation de l'incident
    incident: {
      address: sav.incidentAddress,
      coordinates: sav.incidentLatitude && sav.incidentLongitude ? {
        lat: sav.incidentLatitude,
        lng: sav.incidentLongitude,
      } : null,
      city: sav.incidentCity,
      postalCode: sav.incidentPostalCode,
      region: sav.incidentRegion,
      country: sav.incidentCountry,
    },

    // État de réparation
    repair: {
      status: getRepairStatusLabel(sav.repairStatus),
      hasQuote: sav.hasQuote,
    },

    // Dates
    dates: {
      created: sav.createdAt,
      updated: sav.updatedAt,
      declaration: sav.declarationDate,
      incident: sav.incidentDate,
      draftUpdated: sav.draftUpdatedAt,
    },
  };
}

/**
 * Formate une adresse d'incident
 */
export function formatIncidentAddress(sav: ISavV2): string {
  const parts: string[] = [];
  if (sav.incidentAddress) parts.push(sav.incidentAddress);
  if (sav.incidentPostalCode) parts.push(sav.incidentPostalCode);
  if (sav.incidentCity) parts.push(sav.incidentCity);
  if (sav.incidentRegion) parts.push(sav.incidentRegion);
  if (sav.incidentCountry && sav.incidentCountry !== 'FR') parts.push(sav.incidentCountry);

  return parts.join(', ') || 'Adresse non renseignée';
}